import {z} from 'zod';
import {zDateOnly} from '@brunata-angular/util';

export const INSTALLATION_PROJECTS_FEATURE_FLAG = 'online-installation-projects';

/** The 7-phase lifecycle in its fixed order (AGI-19640). Drives the status filter and sort order
 * and the detail view's progress stepper. `unknown` is deliberately not part of the ordered
 * model: it marks a Dynamics phase Brunata Online does not know, so it sorts after every known
 * phase rather than taking a position within them. Booked precedes planned, although Dynamics
 * numbers them the other way round. */
export const INSTALLATION_PROJECT_STATUSES = [
    'awaiting_customer_info',
    'under_preparation',
    'booked',
    'planned',
    'in_progress',
    'post_processing',
    'completed',
] as const;

export type InstallationProjectStatus = (typeof INSTALLATION_PROJECT_STATUSES)[number] | 'unknown';

const installationProjectStatusSchema: z.ZodType<InstallationProjectStatus> = z
    .enum([...INSTALLATION_PROJECT_STATUSES, 'unknown'])
    .catch('unknown');

export const installationProjectSchema = z.object({
    projectId: z.string().nullable(),
    // The openapi spec declares this non-nullable, but the backend author reports real Dynamics
    // data carries nulls (AGI-19637 comment), and one null row would fail the whole list parse.
    projectNumber: z.string().nullable(),
    buildingName: z.string().nullable(),
    status: installationProjectStatusSchema,
    buildingNumber: z.string().nullable(),
    street: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    plannedStart: zDateOnly.nullable(),
    expectedCompletion: zDateOnly.nullable(),
});

export type InstallationProject = z.infer<typeof installationProjectSchema>;

const installationProjectLocationSummarySchema = z.object({
    total: z.number(),
    inProgress: z.number(),
    completed: z.number(),
    outstanding: z.number(),
});

const installationProjectNextVisitSchema = z.object({
    date: zDateOnly,
    // Dynamics holds the time window as free text, so real rows carry "08:00" as well as "08".
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
});

export const installationProjectDetailSchema = installationProjectSchema.extend({
    customerName: z.string().nullable(),
    projectManagerName: z.string().nullable(),
    salesRepresentativeName: z.string().nullable().optionalUntilDeployed('AGI-19638'),
    locations: installationProjectLocationSummarySchema,
    // Null whenever no uncompleted location carries a visit date.
    nextVisit: installationProjectNextVisitSchema.nullable(),
});

export type InstallationProjectDetail = z.infer<typeof installationProjectDetailSchema>;
export type InstallationProjectNextVisit = z.infer<typeof installationProjectNextVisitSchema>;

export type InstallationProjectStatusDisplay = {
    labelKey: string;
    pillClass: string;
};

export function installationProjectStatusDisplay(
    status: InstallationProjectStatus,
): InstallationProjectStatusDisplay {
    switch (status) {
        case 'awaiting_customer_info':
            return {
                labelKey: 'installationProjects_status_awaitingCustomerInfo',
                pillClass: 'status-awaiting-customer-info',
            };
        case 'under_preparation':
            return {
                labelKey: 'installationProjects_status_underPreparation',
                pillClass: 'status-under-preparation',
            };
        case 'planned':
            return {
                labelKey: 'installationProjects_status_planned',
                pillClass: 'status-planned',
            };
        case 'booked':
            return {
                labelKey: 'installationProjects_status_booked',
                pillClass: 'status-booked',
            };
        case 'in_progress':
            return {
                labelKey: 'installationProjects_status_inProgress',
                pillClass: 'status-in-progress',
            };
        case 'post_processing':
            return {
                labelKey: 'installationProjects_status_postProcessing',
                pillClass: 'status-post-processing',
            };
        case 'completed':
            return {
                labelKey: 'installationProjects_status_completed',
                pillClass: 'status-completed',
            };
        case 'unknown':
            return {
                labelKey: 'installationProjects_status_unknown',
                pillClass: 'status-unknown',
            };
    }
}

/** The visit states a location can carry (AGI-19640 §5.2), in lifecycle order; an unsuccessful
 * visit still needs a visit, so it sorts before completed. The set is closed: the backend
 * collapses unset and unknown Dynamics states to `not_planned`. */
export const INSTALLATION_PROJECT_VISIT_STATUSES = [
    'not_planned',
    'planned',
    'in_progress',
    'partially_completed',
    'unsuccessful_visit',
    'completed',
] as const;

export type InstallationProjectVisitStatus = (typeof INSTALLATION_PROJECT_VISIT_STATUSES)[number];

export const installationProjectLocationSchema = z.object({
    proBraLoc: z.string().nullable().optionalUntilDeployed('AGI-19639'),
    street: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    consumerName: z.string().nullable(),
    email: z.string().nullable(),
    visitDate: zDateOnly.nullable(),
    // Dynamics holds the time window as free text, so real rows carry "08:00" as well as "08".
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
    visitStatus: z.enum(INSTALLATION_PROJECT_VISIT_STATUSES),
    // Null unless Dynamics marks the remarks as shown in Brunata Online.
    remarks: z.string().nullable(),
});

export type InstallationProjectLocation = z.infer<typeof installationProjectLocationSchema>;

export function installationProjectVisitStatusDisplay(
    status: InstallationProjectVisitStatus,
): InstallationProjectStatusDisplay {
    switch (status) {
        case 'not_planned':
            return {
                labelKey: 'installationProjects_visitStatus_notPlanned',
                pillClass: 'status-not-planned',
            };
        case 'planned':
            return {
                labelKey: 'installationProjects_status_planned',
                pillClass: 'status-planned',
            };
        case 'in_progress':
            return {
                labelKey: 'installationProjects_status_inProgress',
                pillClass: 'status-in-progress',
            };
        case 'partially_completed':
            return {
                labelKey: 'installationProjects_visitStatus_partiallyCompleted',
                pillClass: 'status-partially-completed',
            };
        case 'unsuccessful_visit':
            return {
                labelKey: 'installationProjects_visitStatus_unsuccessfulVisit',
                pillClass: 'status-unsuccessful-visit',
            };
        case 'completed':
            return {
                labelKey: 'installationProjects_status_completed',
                pillClass: 'status-completed',
            };
    }
}

/** The visit time window as displayed - Dynamics free text shown as-is, never parsed. A
 * one-sided window has no meaningful rendering, so it reads as no window. */
export function formatVisitTimeSlot(
    startTime: string | null,
    endTime: string | null,
): string | null {
    if (startTime == null || endTime == null) {
        return null;
    }
    return `${startTime} – ${endTime}`;
}
